import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import {
    checkForDuplicateStepName,
    createStepConfig,
    CreateStepDefaults,
    CreateStepFormData,
    CreateStepSchema,
    stepFormStyles,
} from './step-form-helper';
import { pipelineFormStyles as formStyles } from 'pages/pipelines/CreateOrUpdatePipeline/components/pipeline-form-helper';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CreateFormFeatureDialog } from 'components';
import { createPipelineStyles } from 'pages/pipelines/CreateOrUpdatePipeline/create-or-update-pipeline-helper';
import { PIPELINES_AND_STEPS_PATHS } from 'constants/pipelines-and-steps-paths';
import { useLocation, useNavigate } from 'react-router-dom';
import { TextField } from '@biq/apex-ui';
import { useStep } from 'hooks/useSteps';
import { commonShowAlert } from 'lib';
import { useAlert, useServicesEnabled } from 'core/contexts';
import {
    FIELD_MAPPING,
    JAVASCRIPT_TRANSFORM,
    RULE_SET,
    StepsType,
} from 'pages/pipelines/pipelines-helper';
import { FieldMappingForm } from '../createFieldMapping/FieldMappingForm';
import { CreateRuleForm } from '../createRuleSet/CreateRuleForm';
import { CreateJavascriptTransformForm } from '../createJavascriptTransform/CreateJavascriptTransformForm';

export const StepForm = (): React.JSX.Element => {
    const { isServiceEnabledFor } = useServicesEnabled();
    const isDataPipelineServiceEnabled = isServiceEnabledFor('DATA_PIPELINE');
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as CreateStepFormData;
    const queryParams = new URLSearchParams(location.search);
    const option = queryParams.get('option');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFieldMappingModalOpen, setIsFieldMappingModalOpen] =
        useState<boolean>(false);
    const [isRuleSetModalOpen, setIsRuleSetModalOpen] =
        useState<boolean>(false);
    const [isJavascriptTransformModalOpen, setIsJavascriptTransformModalOpen] =
        useState<boolean>(false);
    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
        getValues,
    } = useForm<CreateStepFormData>({
        mode: 'all',
        resolver: yupResolver(CreateStepSchema),
        defaultValues: CreateStepDefaults,
    });
    const type =
        StepsType.find((step) => step.label === option)?.type ??
        StepsType[2].type;
    const { data: stepFieldMappingData } = useStep({ type });
    const { showAlert, hideAlert } = useAlert();

    useEffect(() => {
        if (state) {
            reset({ ...state } as CreateStepFormData);
        }
    }, [state, reset]);

    const handleDuplicateCheck = (data: CreateStepFormData): boolean => {
        const idx = checkForDuplicateStepName(
            stepFieldMappingData?.data ?? [],
            data
        );
        if (idx) {
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                'A step with this name already exists.'
            );
            return true;
        }
        return false;
    };

    const onSubmit = (data: CreateStepFormData): void => {
        if (handleDuplicateCheck(data)) {
            setIsLoading(false);
            return;
        }

        if (option === FIELD_MAPPING) {
            setIsFieldMappingModalOpen(true);
        } else if (option === RULE_SET) {
            setIsRuleSetModalOpen(true);
        } else if (option === JAVASCRIPT_TRANSFORM) {
            setIsJavascriptTransformModalOpen(true);
        }
    };

    const handleBackButtonClick = (): void => {
        navigate(
            `${PIPELINES_AND_STEPS_PATHS.CREATE_NEW_STEP}?option=${option}`
        );
    };

    const handleCloseButtonClick = (): void => {
        navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
    };

    const onFieldMappingModalClose = (): void => {
        setIsFieldMappingModalOpen(false);
    };

    const onRuleSetModalClose = (): void => {
        setIsRuleSetModalOpen(false);
    };

    const onJavascriptTransformModalClose = (): void => {
        setIsJavascriptTransformModalOpen(false);
    };

    if (!isDataPipelineServiceEnabled) {
        navigate(PIPELINES_AND_STEPS_PATHS.LANDING_PAGE);
        return <></>;
    }

    return (
        <>
            <CreateFormFeatureDialog
                buttonText='Next'
                isLoading={isLoading}
                handleSubmit={handleSubmit(onSubmit)}
                handleCloseButton={handleCloseButtonClick}
                handleBackButton={handleBackButtonClick}
                listConfig={createStepConfig}
                customStyles={{
                    ...createPipelineStyles.root,
                    '.headingText': stepFormStyles.formHeadingText,
                }}
                customButtonContainerStyles={
                    stepFormStyles.formFeatureNextButtonStyles
                }
            >
                <Grid sx={formStyles.mainContainer} mt={0.5}>
                    <Box sx={formStyles.fieldGroup}>
                        <Controller
                            name='name'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <TextField
                                    {...field}
                                    variant='outlined'
                                    label='Step Name'
                                    value={field.value}
                                    onChange={(event): void => {
                                        field.onChange(event);
                                    }}
                                    onBlur={(value): void => {
                                        field.onChange(
                                            value.target.value.trim()
                                        );
                                    }}
                                    required
                                    error={!!errors?.name}
                                    errorText={errors?.name?.message ?? ''}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={formStyles.fieldGroup}>
                        <Controller
                            name='description'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <TextField
                                    {...field}
                                    variant='outlined'
                                    label='Description'
                                    value={field.value}
                                    onChange={(event): void => {
                                        field.onChange(event);
                                    }}
                                    onBlur={(value): void => {
                                        field.onChange(
                                            value.target.value.trim()
                                        );
                                    }}
                                    minRows={4}
                                    multiline
                                    error={!!errors?.description}
                                    errorText={
                                        errors?.description?.message ?? ''
                                    }
                                />
                            )}
                        />
                    </Box>
                </Grid>
            </CreateFormFeatureDialog>
            {isFieldMappingModalOpen && (
                <FieldMappingForm
                    isEdit={false}
                    stepData={getValues()}
                    onModalClose={onFieldMappingModalClose}
                />
            )}
            {isRuleSetModalOpen && (
                <CreateRuleForm
                    isEdit={false}
                    stepData={getValues()}
                    onModalClose={onRuleSetModalClose}
                />
            )}
            {isJavascriptTransformModalOpen && (
                <CreateJavascriptTransformForm
                    stepData={getValues()}
                    onModalClose={onJavascriptTransformModalClose}
                />
            )}
        </>
    );
};
